import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCrudEditContext } from "@ryde/redux";
import {
  BulkProduceTypeEnum,
  ICustomField,
  ProductionChainFormFields,
} from "@ryde/shared";

import { DropdownBoundTemplate } from "../dropdown-bound.template";
import { GroupTitleTemplate } from "../group_title.template";
import { SwitchTemplate } from "../switch.template";
import { useNextButtonProductionAction } from "./use-next-button-production-action";

export const ProductionChainLogic: FC<ICustomField> = ({ name }) => {
  const { t } = useTranslation();
  const { id } = useCrudEditContext();
  const { watch, setValue, getValues } = useFormContext();

  const nextProduceButtonFieldName = `${name}.${ProductionChainFormFields.produceButtonId}`;
  const rejectUnassignedFieldName = `${name}.${ProductionChainFormFields.isRejectUnassigned}`;

  const selectedNextButtonId = watch(nextProduceButtonFieldName);
  const { productionAction: nextButtonProductionAction } =
    useNextButtonProductionAction(selectedNextButtonId);

  // "Reject unassigned" only makes sense when the next chained button still leaves
  // passengers unassigned (Join / Join & Produce). A pure Produce next-button would
  // create rides for them, so the toggle is hidden in that case.
  const isRejectUnassignedVisible =
    !!nextButtonProductionAction &&
    nextButtonProductionAction !== BulkProduceTypeEnum.Produce;

  // Clear a stale "reject unassigned" flag once the next button is a pure Produce,
  // so a hidden toggle can never persist an active value. Only write when the flag
  // is currently active, otherwise we would dirty the form for a no-op.
  useEffect(() => {
    const isRejectUnassignedActive = !!getValues(rejectUnassignedFieldName);

    if (
      nextButtonProductionAction === BulkProduceTypeEnum.Produce &&
      isRejectUnassignedActive
    ) {
      setValue(rejectUnassignedFieldName, false, { shouldDirty: true });
    }
  }, [
    nextButtonProductionAction,
    rejectUnassignedFieldName,
    getValues,
    setValue,
  ]);

  return (
    <div className="production-chain-container">
      <GroupTitleTemplate
        name={name}
        labelTranslated={t("produceButton.formLabel.chain.title")}
      />

      <DropdownBoundTemplate
        name={nextProduceButtonFieldName}
        formLabel="Next-auto-action"
        labelTranslated={t("produceButton.formLabel.chain.nextAutoAction")}
        formAttributes={{
          dropItemsSource: "produceButton",
          customDropdownItemsFilter: item => item?.id !== id,
          dropItemsDisableAdd: true,
        }}
      />

      {isRejectUnassignedVisible && (
        <SwitchTemplate
          name={rejectUnassignedFieldName}
          labelTranslated={t("produceButton.formLabel.chain.rejectUnassigned")}
        />
      )}
    </div>
  );
};
